import React, { useState, useEffect } from 'react';
import { Modal, Row, Col, Form } from 'react-bootstrap';
import Button from 'components/base/Button';
import { useHVAConversation } from './HVADynamicAgentConversationContext';
import HVADynamicConversationWidget from './HVADynamicConversationWidget';
import {
  HVADynamicConversationModalProps,
  FeedbackMessage
} from './HVADynamicAgentData';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

const HVADynamicConversationModalContent: React.FC<
  HVADynamicConversationModalProps
> = ({
  show,
  onHide,
  title,
  agentCode,
  agentName,
  agentImage,
  onError,
  customer,
  trn,
  dateOfBirth,
  debtamount,
  currency,
  origin,
  IBAN,
  firstMessage,
  prompt,
  voice,
  language
}) => {
  const {
    setClientInfo,
    isNifValidated,
    isDobValidated,
    requestTransfer,
    setRequestTransfer,
    setIsNifValidated,
    setIsDobValidated
  } = useHVAConversation();

  // Inicializar clientInfo cuando se montan los props
  useEffect(() => {
    if (trn && customer && dateOfBirth && debtamount) {
      setClientInfo({
        trn: trn,
        name: customer,
        dateofbirth: new Date(dateOfBirth),
        debtamount: Number(debtamount),
        currency: currency || 'EUR',
        origin: origin || '',
        IBAN: IBAN || 'GR16 0110 1050 0025 4801 2345 678'
      });
    }
  }, [trn, customer, dateOfBirth, debtamount, setClientInfo]);

  const [formData, setFormData] = useState({
    trn: trn || '',
    name: customer || '',
    // eslint-disable-next-line
    dateofbirth: dateOfBirth ? new Date(dateOfBirth).toISOString().split('T')[0] : '',
    debtamount: debtamount?.toString() || '0'
  });

  const [feedbackMessages, setFeedbackMessages] = useState<FeedbackMessage[]>(
    []
  );

  // Función para añadir mensajes de feedback
  const addFeedbackMessage = (
    message: Omit<FeedbackMessage, 'id' | 'timestamp'>
  ) => {
    setFeedbackMessages(prev => [
      {
        ...message,
        id: Math.random().toString(36).substring(2, 11),
        timestamp: new Date()
      },
      ...prev
    ]); // Añadimos el nuevo mensaje al principio del array
  };

  // Función para resetear el estado
  const resetState = () => {
    setFormData({
      trn: trn || '',
      name: customer || '',
      // eslint-disable-next-line
      dateofbirth: dateOfBirth ? new Date(dateOfBirth).toISOString().split('T')[0] : '',
      debtamount: debtamount?.toString() || '0'
    });
    setFeedbackMessages([]);
    setIsNifValidated(false);
    setIsDobValidated(false);
    setRequestTransfer(false);
    setClientInfo({
      trn: trn || '',
      name: customer || '',
      dateofbirth: dateOfBirth || new Date(),
      debtamount: debtamount || 0,
      currency: currency || 'EUR',
      origin: origin || '',
      IBAN: IBAN || 'GR16 0110 1050 0025 4801 2345 678'
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLElement>) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement;
    const { name, value } = target;

    // Manejo especial para el tipo de préstamo
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    setClientInfo({
      trn: formData.trn,
      name: formData.name,
      // eslint-disable-next-line
      dateofbirth: formData.dateofbirth ? new Date(formData.dateofbirth) : new Date(),
      debtamount: Number(formData.debtamount),
      currency: currency || 'EUR',
      origin: origin || '',
      IBAN: IBAN || 'GR16 0110 1050 0025 4801 2345 678'
    });

    setIsNifValidated(false);
    setIsDobValidated(false);

    setFeedbackMessages(prev => [
      {
        id: Math.random().toString(36).substring(2, 11),
        timestamp: new Date(),
        type: 'validation',
        message: 'Customer information updated',
        details: `Name: ${formData.name} .... TRN: ${formData.trn} .... DOB: ${formData.dateofbirth} .... Debt: ${formData.debtamount} ${currency}`,
        success: true
      },
      ...prev
    ]);
  };

  const handleCloseModal = () => {
    resetState();
    onHide();
  };

  const handleTransfer = async () => {
    try {
      // Resetear todos los estados
      resetState();

      // Añadir mensaje de feedback sobre la transferencia
      const newFeedbackMessage = {
        id: Math.random().toString(36).substring(2, 11),
        timestamp: new Date(),
        type: 'close',
        message: 'Llamada finalizada',
        details: `TRN: ${formData.trn} - Nombre: ${formData.name}`,
        success: true
      } as FeedbackMessage;

      setFeedbackMessages(messages => [newFeedbackMessage, ...messages]);
    } catch (error) {
      console.error('Error al transferir la llamada:', error);
      onError?.(error as Error);
    }
  };

  const renderTransferAlert = () => {
    if (!requestTransfer) return null;

    return (
      <div className="d-flex align-items-center fs-9 pt-2">
        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
        <div className="flex-grow-1">Request to Transfer Call</div>
        <Button variant="warning" size="sm" onClick={handleTransfer}>
          Transfer Call
        </Button>
      </div>
    );
  };

  const renderFeedbackPanel = () => {
    return (
      <>
        <h5 className="pb-0 pt-4">{`${agentName}'s feedback`}</h5>
        <p className="fs-9 lh-sm">
          The panel below displays requests for information or tool usage made
          during the conversation.
        </p>
        <div
          className="p-3 pt-0 border rounded scrollbar bg-body-emphasis shadow-sm"
          style={{
            height: '100px',
            overflowY: 'auto',
            scrollBehavior: 'smooth'
          }}
        >
          {renderTransferAlert()}

          {/* Mensajes de feedback de las tools */}
          <div className="mt-3">
            {feedbackMessages.map(msg => (
              <div
                key={msg.id}
                className={`alert ${
                  msg.success ? 'bg-body' : 'alert-warning'
                } mb-2 py-2 px-3`}
                role="alert"
              >
                <div className="d-flex flex-column">
                  <small className="text-muted fs-10">
                    {msg.timestamp.toLocaleTimeString()}
                  </small>
                  <div className="fs-9 text-primary lh-1">{msg.message}</div>
                  {msg.details && (
                    <small className="mt-1 text-muted fs-10 lh-1">
                      {msg.details}
                    </small>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </>
    );
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="xl"
      animation={true}
      contentClassName="border-0"
    >
      <Modal.Header closeButton>
        <Modal.Title className="text-body-emphasis">
          {`${title} - ${agentName}`}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-0 pe-3 bg-body">
        <Row className="g-0">
          <h3 className="pb-0 pt-4 px-6">Test scenario</h3>
          <p className="fs-8 lh-sm px-6 mb-0">
            This test scenario demonstrates the interaction between the agent,
            the customer, and the system. The agent starts the call without any
            prior information, which is provided incrementally as needed during
            the interaction.
          </p>
          {/* Columna izquierda: Info inicial y solicitudes del agente durante la conversación */}
          <Col xs={1} xxl={1} className="p-4 px-6 pe-0 d-none">
            <h5 className="pb-0 pt-2">Data to verify during the call</h5>
            <p className="fs-9 lh-sm">
              The TRN and date of birth are not known to the agent. The agent
              will only collect the information provided by the customer and ask
              for validation to the system.
            </p>
            <Form onSubmit={handleSubmit} className="w-100">
              <div className="d-flex flex-column gap-2">
                {/* Primera fila - Nombre completo */}
                <Form.Floating>
                  <Form.Control
                    className="shadow-sm"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Full name"
                  />
                  <label>Full name</label>
                </Form.Floating>

                {/* Segunda fila - NIF y Fecha de nacimiento */}
                <div className="d-flex gap-2">
                  <Form.Floating className="flex-grow-1">
                    <Form.Control
                      className="shadow-sm"
                      type="text"
                      name="trn"
                      value={formData.trn}
                      onChange={handleInputChange}
                      placeholder="TRN"
                      isValid={isNifValidated}
                    />
                    <label>TRN</label>
                  </Form.Floating>

                  <Form.Floating className="flex-grow-1">
                    <Form.Control
                      className="shadow-sm"
                      type="date"
                      name="dateofbirth"
                      value={formData.dateofbirth}
                      onChange={handleInputChange}
                      placeholder="Birth date"
                      isValid={isDobValidated}
                    />
                    <label>Birth date</label>
                  </Form.Floating>
                </div>

                {/* Tercera fila - Tipo de préstamo y botón */}
                <div className="d-flex gap-2">
                  <Form.Floating className="flex-grow-1">
                    <Form.Control
                      className="shadow-sm"
                      type="number"
                      name="debtamount"
                      value={formData.debtamount}
                      onChange={handleInputChange}
                      placeholder="Debt amount"
                      min="0"
                      step="0.01"
                    />
                    <label>Debt amount (€)</label>
                  </Form.Floating>

                  <Button
                    type="submit"
                    variant="primary"
                    style={{ minWidth: '100px' }}
                  >
                    Update
                  </Button>
                </div>
              </div>
            </Form>
          </Col>

          {/* Columna centro: Imagen */}
          <Col md={2} className="d-flex p-4 pe-0 pb-0 align-items-end">
            {agentImage && (
              <div className="text-center mb-2">
                <img
                  src={agentImage}
                  alt={agentName}
                  className="img-fluid rounded"
                  style={{
                    width: '100%',
                    minWidth: '163px',
                    maxWidth: '163px',
                    height: 'auto'
                  }}
                />
              </div>
            )}
          </Col>

          {/* Columna derecha: Datos dinámicos, widget de Conversación e información dinámica */}
          <Col md={9} className="p-4">
            <h5 className="pb-0 pt-2">Call with {agentName}</h5>
            <p className="fs-9 lh-sm">
              Press the button below to start a conversation with the agent. For
              the Proof of Concept (POC) to function correctly, it is necessary
              to have selected a TRN beforehand for its verification during the
              conversation.
            </p>
            <HVADynamicConversationWidget
              agentId={agentCode}
              onError={onError}
              onFeedback={addFeedbackMessage}
              firstMessage={firstMessage}
              prompt={prompt}
              voice={voice}
              language={language}
            />
            {renderFeedbackPanel()}
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="primary" onClick={handleCloseModal}>
          Close and reset
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default HVADynamicConversationModalContent;
